import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, Building2, CheckCircle2, Home, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { directoryRegions } from "@/data/directory-pages";
import { cityGeo, cityPages, getCityPage } from "@/data/city-pages";
import { breadcrumbLd, geoMeta, jsonLdScript, SITE_URL } from "@/lib/json-ld";

export const Route = createFileRoute("/immobilie-verkaufen/$stadt")({
  staticData: { sitemap: true },
  loader: ({ params }) => {
    const city = getCityPage(params.stadt);
    if (!city) throw notFound();
    return { city };
  },
  head: ({ loaderData }) => {
    const city = loaderData?.city;
    const title = city
      ? `Immobilie verkaufen in ${city.name} | Begner`
      : "Immobilie verkaufen | Begner";
    const description = city
      ? `Haus, Wohnung oder Mehrfamilienhaus in ${city.name} verkaufen: diskret, ohne Vorabkosten und mit geprüften Käufern aus dem Kanton ${city.cantonName}.`
      : "Immobilie in der Schweiz diskret verkaufen.";
    const geo = city ? cityGeo[city.slug] : undefined;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        ...(city && geo
          ? geoMeta(geo.lat, geo.lng, `CH-${city.canton.toUpperCase()}`, city.name)
          : []),
      ],
      scripts: city
        ? [
            jsonLdScript({
              "@context": "https://schema.org",
              "@type": "Service",
              name: `Immobilie verkaufen in ${city.name}`,
              serviceType: "Immobilienverkauf",
              url: `${SITE_URL}/immobilie-verkaufen/${city.slug}`,
              provider: { "@type": "Organization", name: "Begner", url: SITE_URL },
              areaServed: {
                "@type": "City",
                name: city.name,
                containedInPlace: {
                  "@type": "AdministrativeArea",
                  name: `Kanton ${city.cantonName}`,
                },
                ...(geo
                  ? { geo: { "@type": "GeoCoordinates", latitude: geo.lat, longitude: geo.lng } }
                  : {}),
              },
            }),
            jsonLdScript(
              breadcrumbLd([
                { name: "Startseite", path: "/" },
                { name: "Immobilien", path: "/immobilien" },
                { name: `Verkaufen in ${city.name}`, path: `/immobilie-verkaufen/${city.slug}` },
              ]),
            ),
          ]
        : [],
    };
  },
  notFoundComponent: () => (
    <div className="container-page py-20 text-center">
      <h1 className="font-display text-3xl font-bold">Region nicht gefunden</h1>
      <Button asChild className="mt-6 rounded-xl">
        <Link to="/immobilien">Alle Immobilien</Link>
      </Button>
    </div>
  ),
  component: SellCityView,
});

function SellCityView() {
  const { city } = Route.useLoaderData();
  const directoryRegion = directoryRegions.find((region) => region.canton === city.canton)!;
  const points = [
    `Diskreter Verkauf ohne öffentliche Inserate, wenn Sie das wünschen.`,
    `Interessenten und Investoren mit Fokus auf den Kanton ${city.cantonName}.`,
    "Keine Vorabkosten, Sie entscheiden frei über jedes Angebot.",
    "Persönliche Begleitung von der Bewertung bis zur Beurkundung.",
  ];
  const steps = [
    "Objekt kurz beschreiben, mit Lage, Fläche und Fotos.",
    `Wir prüfen die Angaben und schätzen den Marktwert in ${city.name}.`,
    "Passende Käufer erhalten Ihr Objekt, auf Wunsch anonym.",
  ];

  return (
    <>
      <section className="border-b border-border bg-surface py-14 md:py-20">
        <div className="container-page max-w-4xl">
          <p className="inline-flex items-center gap-2 text-sm font-semibold text-brand-deep">
            <MapPin className="h-4 w-4" /> Kanton {city.cantonName}
          </p>
          <h1 className="mt-3 font-display text-4xl font-bold leading-tight md:text-5xl">
            Immobilie verkaufen in {city.name}
          </h1>
          <p className="mt-5 text-lg leading-8 text-muted-foreground">
            Sie möchten ein Haus, eine Wohnung oder ein Mehrfamilienhaus in {city.name} verkaufen?
            Begner verbindet Eigentümer mit geprüften Käufern aus der Region und begleitet den
            Verkauf diskret.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="rounded-xl">
              <Link to="/mehrfamilienhaus-verkaufen">
                Objekt kostenlos anbieten <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-xl bg-card">
              <Link to="/immobilien">Immobilien ansehen</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-16">
        <div className="container-page grid max-w-4xl gap-10 md:grid-cols-2">
          <div>
            <h2 className="font-display text-2xl font-bold">Ihre Vorteile in {city.name}</h2>
            <ul className="mt-5 space-y-3">
              {points.map((point) => (
                <li key={point} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
                  <span className="text-sm leading-6">{point}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm leading-6 text-muted-foreground">
              Vor dem Verkauf lohnt sich ein Blick auf den Zustand:{" "}
              {city.topics.slice(0, 2).join(" und ")} sind in {city.name} häufige Themen und
              beeinflussen den Preis.
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
            <h2 className="font-display text-2xl font-bold">So läuft der Verkauf</h2>
            <ol className="mt-5 space-y-4">
              {steps.map((step, index) => (
                <li key={step} className="flex gap-3">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-soft text-sm font-bold text-brand-deep">
                    {index + 1}
                  </span>
                  <span className="text-sm leading-6">{step}</span>
                </li>
              ))}
            </ol>
            <Button asChild variant="link" className="mt-4 px-0">
              <Link
                to="/artikel/$slug"
                params={{ slug: "eigenmietwert-berechnen-rechner-schweiz" }}
              >
                Eigenmietwert berechnen
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-surface py-12">
        <div className="container-page max-w-4xl">
          <div className="mb-8 rounded-2xl border border-border bg-card p-6">
            <h2 className="font-display text-xl font-bold">
              Lieber einen lokalen Makler vergleichen?
            </h2>
            <p className="mt-3 text-sm leading-7 text-muted-foreground">
              Im Maklerverzeichnis finden Sie veröffentlichte Profile und Hinweise zur Auswahl im
              Kanton {city.cantonName}.
            </p>
            <Link
              to="/makler/$region"
              params={{ region: directoryRegion.slug }}
              className="mt-4 inline-flex min-h-11 items-center gap-2 font-semibold text-brand-deep hover:underline"
            >
              Makler in {city.cantonName} ansehen <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <h2 className="font-display text-xl font-bold">Vor dem Verkauf renovieren?</h2>
          <div className="mb-10 mt-5 flex flex-wrap gap-3">
            <Link
              to="/handwerker/$stadt"
              params={{ stadt: city.slug }}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium hover:border-brand hover:text-brand-deep"
            >
              <Home className="h-4 w-4 text-brand" /> Handwerker in {city.name}
            </Link>
          </div>
          <h2 className="font-display text-xl font-bold">Immobilie verkaufen in der Nähe</h2>
          <div className="mt-5 flex flex-wrap gap-3">
            {cityPages
              .filter((c) => city.nearby.includes(c.slug))
              .map((other) => (
                <Link
                  key={other.slug}
                  to="/immobilie-verkaufen/$stadt"
                  params={{ stadt: other.slug }}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium hover:border-brand hover:text-brand-deep"
                >
                  <Building2 className="h-4 w-4 text-brand" /> {other.name}
                </Link>
              ))}
          </div>
        </div>
      </section>
    </>
  );
}
